/**
 * A new gym's document — `gyms/{gymId}` at creation — and the slug it is
 * filed under.
 *
 * Two writers create gyms: the platform admin's "New gym" flow
 * (`PlatformAdmin.jsx` `handleGymCreate`, client SDK under the rules) and the
 * MCP server's `create_gym` tool (Admin SDK, which the rules do not see). Both
 * build the document here, so an AI-created gym cannot drift from the shape
 * the app creates (docs/spec/20b-mcp-tools.md "Platform-műveletek").
 *
 * Pure: no timestamps. `createdAt` / `updatedAt` are the caller's
 * `serverTimestamp()`. Input validation is not here either — the app's create
 * form passes a handful of fields, the MCP validates its own with zod.
 */
import { PLAN_FEATURES } from "./planFeatures.js";

/** The seven keys of a gym's weekly `openingHours`, Monday first. */
export const OPENING_DAY_KEYS = Object.freeze(["mon", "tue", "wed", "thu", "fri", "sat", "sun"]);

/** Size of an empty, image-less map row — what the admin panel's "New map" button writes. */
export const NEW_MAP_SIZE = Object.freeze({ width: 520, height: 480 });

/** Generate a URL-safe slug from a name string. */
export function generateSlug(name) {
  return (name || "")
    .normalize("NFD").replace(/[\u0300-\u036f]/g, "") // strip diacritics
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-") // non-alphanumeric → hyphen
    .replace(/-+/g, "-") // dedupe hyphens
    .replace(/^-|-$/g, "") // trim leading/trailing
    .slice(0, 40) || "gym";
}

/**
 * The `features` map of a new gym on `plan`. `"off"` keeps the plan's numeric
 * limits but switches every boolean feature off — the app's create flow, where
 * the operator turns features on after onboarding. `"plan"` is the plan's full
 * default, what a plan change writes (the MCP `create_gym`).
 */
function initialFeatures(plan, featureDefaults) {
  const features = {};
  for (const [k, v] of Object.entries(PLAN_FEATURES[plan])) {
    features[k] = featureDefaults === "plan" || typeof v !== "boolean" ? v : false;
  }
  return features;
}

/**
 * Build a new gym document.
 *
 * `input`: `name`, `slug` (missing → `generateSlug(name)`), `plan` (unknown →
 * `free`), `accentColor`, `createdBy`, and the optional setup fields —
 * `description`, `address`, `city`, `country` (default `HU`), `location`,
 * `contactEmail`, `contactPhone`, `website`, `socialLinks`, `timezone`,
 * `openingHours`, `disciplines`, `maps` (`[{ name }]`), `customDifficulty`,
 * `difficultyByDiscipline`, `customHoldColors`. A field the input leaves out
 * gets the app's default, or is not written at all where the app does not
 * write it (`timezone` — the onboarding "hours" step asks for it; `disciplines`
 * — a missing list means boulder; the ladders and hold colours — a missing one
 * means the built-in set).
 *
 * `opts.now` (ms) dates the billing quarter and the map ids (`map_<ms>`, the
 * admin panel's format, one ms apart per map); `opts.featureDefaults` is
 * `"off"` (default) or `"plan"`, see `initialFeatures`.
 */
export function buildGymDoc(input, { now = Date.now(), featureDefaults = "off" } = {}) {
  const src = input || {};
  const plan = Object.prototype.hasOwnProperty.call(PLAN_FEATURES, src.plan) ? src.plan : "free";
  const at = new Date(now);
  const social = src.socialLinks || {};

  const settings = {
    mapImageUrl: "",
    mapWidth: NEW_MAP_SIZE.width,
    mapHeight: NEW_MAP_SIZE.height,
    tvSlideDuration: 12,
    newRouteDays: 7,
    tvTheme: "dark",
    tvLanguage: "hu",
  };
  if (Array.isArray(src.maps) && src.maps.length) {
    settings.maps = src.maps.map((m, i) => ({
      id: `map_${now + i}`,
      name: String(m.name || ""),
      imageUrl: "",
      width: NEW_MAP_SIZE.width,
      height: NEW_MAP_SIZE.height,
    }));
  }
  if (src.customDifficulty) settings.customDifficulty = src.customDifficulty;
  if (src.difficultyByDiscipline) settings.difficultyByDiscipline = src.difficultyByDiscipline;
  if (src.customHoldColors) settings.customHoldColors = src.customHoldColors;

  const doc = {
    name: src.name,
    slug: src.slug || generateSlug(src.name),
    description: src.description || "",
    address: src.address || "",
    city: src.city || "",
    country: src.country || "HU",
    location: src.location
      ? { latitude: src.location.latitude, longitude: src.location.longitude }
      : null,
    logoUrl: "",
    coverImageUrl: "",
    contactEmail: src.contactEmail || "",
    contactPhone: src.contactPhone || "",
    website: src.website || "",
    socialLinks: { facebook: social.facebook || "", instagram: social.instagram || "" },
    openingHours: src.openingHours
      ? Object.fromEntries(OPENING_DAY_KEYS.map((d) => [d, src.openingHours[d] || null]))
      : Object.fromEntries(OPENING_DAY_KEYS.map((d) => [d, null])),
    accentColor: src.accentColor,
    plan,
    planExpiresAt: null,
    features: initialFeatures(plan, featureDefaults),
    billing: {
      currency: "HUF",
      currentMauBandId: "band_0",
      quarterStart: `${at.getFullYear()}-Q${Math.floor(at.getMonth() / 3) + 1}`,
      lastEvaluatedAt: null,
    },
    isPublic: false,
    isActive: false,
    settings,
    createdBy: src.createdBy || "",
  };
  if (src.timezone) doc.timezone = src.timezone;
  if (Array.isArray(src.disciplines) && src.disciplines.length) doc.disciplines = [...src.disciplines];
  return doc;
}
